/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Card, Stack } from '@mui/material';
import React from 'react';
import { useResizable } from 'react-resizable-layout';
import { ArrowDropDownDown } from '@opengeoweb/theme';

export interface SoundingsAccordionProps {
  name: string;
  minWidth?: number;
  maxWidth?: number;
  accordionIsOpen?: boolean;
  diagramTypeButton?: React.ReactElement;
  handleButtonComponent?: React.ReactElement;
  children?: React.ReactNode;
  setSoundingsIsClosed?: (isClosed: boolean) => void;
  setPreviousHeight?: (size: number) => void;
  size?: number;
  isDisplayed?: boolean;
}

const ACCORDION_MIN_WIDTH = 24;

export const SoundingsAccordion = React.forwardRef<
  HTMLDivElement,
  SoundingsAccordionProps
>(
  (
    {
      name,
      minWidth = ACCORDION_MIN_WIDTH,
      maxWidth,
      accordionIsOpen,
      diagramTypeButton,
      handleButtonComponent,
      children,
      setSoundingsIsClosed,
      setPreviousHeight,
      size,
      isDisplayed,
    }: SoundingsAccordionProps,
    aspectRatioRef,
  ): React.JSX.Element => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const widthRef = React.useRef<number | null>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const [autoSizing, setAutoSizing] = React.useState(false);
    const hasWrittenInitialHeightRef = React.useRef(false);

    // One-time initial write: when component is open and size is available
    React.useLayoutEffect(() => {
      if (!hasWrittenInitialHeightRef.current && isOpen && size != null) {
        size && setPreviousHeight?.(size);
        if (size > ACCORDION_MIN_WIDTH) {
          hasWrittenInitialHeightRef.current = true;
        }
      }
    }, [isOpen, size, setPreviousHeight]);

    React.useEffect(() => {
      if (!isOpen) {
        setSoundingsIsClosed?.(true);
      } else {
        setSoundingsIsClosed?.(false);
      }
    }, [isOpen, setSoundingsIsClosed]);

    const {
      position: containerWidth,
      setPosition: setContainerWidth,
      separatorProps,
    } = useResizable({
      containerRef,
      axis: 'x',
      min: minWidth,
      max: maxWidth || undefined,
      initial: minWidth,
      onResizeStart: () => {
        setAutoSizing(false);
        size && setPreviousHeight?.(size);
      },
      onResizeEnd: ({ position }) => {
        if (position <= minWidth) {
          widthRef.current = null;
          return;
        }
        widthRef.current = position;
        size && setPreviousHeight?.(size);
      },
    });

    const openAccordion = (): void => {
      if (widthRef.current != null) {
        setAutoSizing(false);
        setContainerWidth(Math.max(widthRef.current, minWidth));
        setIsOpen(true);
        return;
      }
      setAutoSizing(true);
      setIsOpen(true);
    };

    const closeAccordion = React.useCallback(() => {
      // Persist the D3 size immediately to avoid render lag downstream
      size && setPreviousHeight?.(size);
      // Inform parent it's closing before toggling local state
      setSoundingsIsClosed?.(true);

      setContainerWidth(minWidth);
      setIsOpen(false);
      setAutoSizing(false);
    }, [
      minWidth,
      setContainerWidth,
      setPreviousHeight,
      setSoundingsIsClosed,
      size,
    ]);
    React.useEffect(() => {
      if (accordionIsOpen) {
        setIsOpen(true);
        setAutoSizing(true);
      }
    }, [accordionIsOpen]);

    React.useLayoutEffect(() => {
      if (autoSizing) {
        return;
      }
      if (!isOpen && containerWidth > minWidth) {
        setIsOpen(true);
        setSoundingsIsClosed?.(false);
        return;
      }
      if (isOpen && containerWidth <= minWidth) {
        setIsOpen(false);
        setAutoSizing(false);
        setSoundingsIsClosed?.(true);
      }
    }, [
      autoSizing,
      containerWidth,
      isOpen,
      minWidth,
      setContainerWidth,
      setSoundingsIsClosed,
    ]);

    React.useLayoutEffect(() => {
      if (!autoSizing || !containerRef.current) {
        return;
      }
      let raf1 = 0;
      let raf2 = 0;

      const measureContainerWidth = (): void => {
        if (!containerRef.current) {
          return;
        }
        const { width } = containerRef.current.getBoundingClientRect();
        const calculatedWidth = Math.max(Math.round(width), minWidth);
        setContainerWidth(calculatedWidth);
        widthRef.current = calculatedWidth;
        setAutoSizing(false); // stop autosizing; lock width now that we have measured it
      };

      // Use double requestAnimationFrame so layout paints before measuring
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(measureContainerWidth);
      });

      // eslint-disable-next-line consistent-return
      return (): void => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }, [autoSizing, minWidth, setContainerWidth]);

    const cardStyleWhenAuto = {
      display: isDisplayed ? 'block' : 'none',
      width: 'auto',
      flex: '1 1 100%',
      minWidth: 0,
      maxWidth: maxWidth || '100%',
      boxSizing: 'border-box',
    };

    const cardStyleWhenLocked = {
      display: isDisplayed ? 'block' : 'none',
      width: containerWidth,
      flex: '0 1 auto',
      minWidth,
      maxWidth: maxWidth || '100%',
      boxSizing: 'border-box',
    };
    //
    return (
      <Card
        className="sortable-item"
        data-autosizing={autoSizing ? 'true' : 'false'}
        data-testid="soundings-accordion"
        ref={containerRef}
        sx={{
          height: '100%',
          position: 'relative',
          ...(autoSizing ? cardStyleWhenAuto : cardStyleWhenLocked),
        }}
      >
        <Box ref={aspectRatioRef} sx={{ height: '100%', width: '100%' }}>
          {!isOpen && (
            <Box
              data-testid="closed-accordion-header"
              onClick={openAccordion}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                writingMode: 'vertical-lr',
                transform: 'rotate(180deg)',
                paddingBottom: '10px',
                cursor: 'pointer',
              }}
            >
              {name}
              <ArrowDropDownDown sx={{ transform: 'rotate(90deg)' }} />
              <Box onClick={(event): void => event.stopPropagation()}>
                {handleButtonComponent}
              </Box>
            </Box>
          )}
          {!!isOpen && (
            <Box data-testid="open-accordion-header">
              <Stack>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    onClick={(event): void => event.stopPropagation()}
                    sx={{ paddingTop: '8px' }}
                  >
                    {handleButtonComponent}
                  </Box>
                  <Box
                    onClick={closeAccordion}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      paddingRight: '10px',
                      overflow: 'hidden',
                      marginTop: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    <Box onClick={(event): void => event.stopPropagation()}>
                      {!!diagramTypeButton && diagramTypeButton}
                    </Box>
                    <Box
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        marginLeft: '16px',
                        height: '100%',
                        paddingTop: '3px',
                      }}
                    >
                      {name}
                    </Box>
                    <ArrowDropDownDown />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {children}
                </Box>
              </Stack>
            </Box>
          )}
          <Box
            data-testid="soundings-accordion-drag-handle"
            {...separatorProps}
            sx={{
              height: '100%',
              position: 'absolute',
              right: 0,
              top: 0,
              width: '5px',
              cursor: 'col-resize',
            }}
          />
        </Box>
      </Card>
    );
  },
);
